import { format as formatDate } from '@schir-int-client/tech';
import { isEmpty, isNil, isNumber, isUndefined } from 'lodash-es';
import { Moment } from 'moment';
import { Textbaustein } from './textbaustein.model';
import { formatNumber } from '@angular/common';

/**
 * Achtung! Behandlung der Texte im Textbaustein ist historisch bedingt doppelt implementiert. Die andere Implementierung
 * liegt in TextbausteinProcessor.java. Beide Implementierungen, solange vorhanden, müssen sich gleich verhalten!
 */

const DATE_VARS: string[] = [
	'VON',
	'BIS',
	'MIT_WIRKUNG_VOM',
	'GENEHMIGUNG_AM',
	'ANTRAGS_DATUM',
	'BESCHLUSS_AM',
	'TESTAMENT_DATUM',
	'BASIS_ZINSSATZ_DATUM',
	'BEWILLIGT_AM',
	'ANTRAGSDATUM',
	'ABTRETUNGSDATUM',
	'ABTRETUNGS_DATUM',
	'URSPRUENGLICH_EINGETRAGEN_AM',
	'EROEFFNUNGS_DATUM',
	'DATUM_GERICHT',
	'DATUM_OLG',
	'ERSUCHDATUM',
	'BEWILLIGUNGSDATUM',
	'DATUM_VERZINSUNG1',
	'DATUM_VERZINSUNG2',
	'DATUM_INSO',
];

const SUB_VARS: string[] = [
	'VOLLSTRECKUNG',
	'GESAMTHAFT',
	'ZINSEN',
	'RANGVERMERK',
	'MIGRATION',
	'VAW_VERFUEGUNG',
	'BEWILLIGUNG_VERFUEGUNG',
	'EINSCHRAENKUNG_EIGENTUEMER',
	'OLG',
	'VORMERKUNG',
	'VERZINSUNG',
	'RANGVORBEHALT',
	'RANGEINTRAG',
	'VORRANGSVORBEHALT',
	'VORERBE',
];

const OPTIONAL_PLATZHALTER = {
	'Registerpfandrecht Verwaltungszwangsverfahren': ['VOLLSTRECKUNGSTITEL', 'VOLLSTRECKUNGSAKTENZEICHEN'],
	'Erbschein': ['TESTAMENTSVOLLSTRECKUNG'],
	'Testament': ['TESTAMENTSVOLLSTRECKUNG'],
};

const BOOLEAN_VARS = {
	TESTAMENTSVOLLSTRECKUNG: (v: boolean) => (v ? '. Testamentsvollstreckung ist angeordnet' : ''),
};

const DEPENDENT_VARS = {
	GENEHMIGUNG: (value: string) => {
		switch (value) {
			case 'Antrag':
				return 'des Antrages';
			case '':
			case null:
			case undefined:
				return 'der #GENEHMIGUNG#';
			default:
				return 'der ' + value;
		}
	},
};

const POTENTIALLY_NATURAL_NUMBER_FIELDS: string[] = ['PARAGRAPH'];

const NO_FILL_UP_NUMBER_FIELDS: string[] = ['ZINSSATZ', 'ZINSSATZ1', 'ZINSSATZ2', 'VV_ZINSSATZ', 'PROZENTSATZ'];

const PLATZHALTER_REGEX: RegExp = /#([A-Z\d_]+)(\[(\d+)]\.([A-Z\d_]+))?#/g;

export type platzhalterWertType = string | number | boolean | Moment | Date | Array<object>;

export function parse(
	textbaustein: Textbaustein,
	platzhalterWerte: { [fieldName: string]: platzhalterWertType },
	subTexte?: { [subField: string]: string }
): string {
	const toParse: string = isEmpty(textbaustein.editedText) ? textbaustein.text : textbaustein.editedText;
	if (isNil(toParse)) {
		return '';
	}

	return parseText(textbaustein, toParse, platzhalterWerte, subTexte);
}

function parseText(
	textbaustein: Textbaustein,
	toParse: string,
	platzhalterWerte: { [fieldName: string]: platzhalterWertType },
	subTexte?: { [subField: string]: string }
) {
	return toParse.replace(PLATZHALTER_REGEX, (platzhalterGroup, fieldGroup, _, indexGroup, attrFieldGroup) => {
		let value: platzhalterWertType;
		if (Object.keys(DEPENDENT_VARS).includes(fieldGroup)) {
			value = DEPENDENT_VARS[fieldGroup](platzhalterWerte[convertToFieldName(fieldGroup)]);
		} else {
			value = platzhalterWerte[convertToFieldName(fieldGroup)];
		}
		if (isNil(value)) {
			return isOptional(fieldGroup, textbaustein.name) ? '' : platzhalterGroup;
		}
		return getPlatzhalterValue(value, platzhalterGroup, fieldGroup, indexGroup, attrFieldGroup, subTexte);
	});
}

function getPlatzhalterValue(
	platzhalterWert: platzhalterWertType,
	platzhalterGroup: string,
	fieldGroup: string,
	indexGroup: number | undefined,
	attrFieldGroup: string | undefined,
	subTexte: { [subField: string]: string }
): string {
	if (SUB_VARS.includes(fieldGroup) && !isUndefined(subTexte)) {
		return format(subTexte[fieldGroup], attrFieldGroup ?? fieldGroup, platzhalterGroup);
	}
	if (!isUndefined(attrFieldGroup)) {
		return format(
			getListFieldValue(<object>platzhalterWert[indexGroup], attrFieldGroup),
			attrFieldGroup,
			platzhalterGroup
		);
	} else {
		return format(platzhalterWert, fieldGroup, platzhalterGroup);
	}
}

function getListFieldValue(platzhalterValueObject: object, platzhalterName: string) {
	return platzhalterValueObject[convertToFieldName(platzhalterName)];
}

function convertToFieldName(platzhalterName: string): string {
	return platzhalterName.toLowerCase().replace(/_([^_])/g, (_, v) => v.toUpperCase());
}

function format(value: any, platzhalterName: string, platzhalterGroup: string): string {
	if (isNil(value)) {
		return platzhalterGroup;
	}
	if (DATE_VARS.includes(platzhalterName)) {
		return formatDate(value);
	}
	if (BOOLEAN_VARS[platzhalterName]) {
		return BOOLEAN_VARS[platzhalterName](value);
	}
	if (isNumber(value)) {
		if (isInteger(platzhalterName, value) || NO_FILL_UP_NUMBER_FIELDS.includes(platzhalterName)) {
			return formatNumberLocale(value, '1.0-5');
		}
		return formatNumberLocale(value);
	}
	if (isEmpty(value)) {
		return platzhalterGroup;
	}
	return value;
}

function isInteger(platzhalterName: string, value: number) {
	return POTENTIALLY_NATURAL_NUMBER_FIELDS.includes(platzhalterName) && Number.isInteger(value);
}

/**
 * Optionale Platzhalter werden, falls Wert null oder undefined, durch Leerstring ersetzt. Diese Methode ist nur für einfache Strings gedacht.
 * Gruppen, Listen usw. haben ihre eigene Behandlung.
 */
function isOptional(platzhalterName: any, textbausteinName: string) {
	return OPTIONAL_PLATZHALTER[textbausteinName]?.includes(platzhalterName);
}

function formatNumberLocale(value: number, digitsInfo?: string) {
	return isNaN(value) ? '' : formatNumber(value, 'de-DE', digitsInfo ?? '1.2-5');
}
